import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { IAppPermissionsSkill, useCreateSkillMutation } from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { SkillForm } from 'skill/components/SkillForm';
import { TSkillFormValues } from 'skill/types';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';

interface IProps extends IPageDrawerProps {
    onCreate?(skillId?: string, formValues?: TSkillFormValues): void;
    onClose?(): void;
}

export const SkillCreateDrawer = ({
    open,
    onCreate,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [createSkill, { loading: createSkillLoading }] =
        useCreateSkillMutation();
    const { canCreate = false }: IAppPermissionsSkill['management'] =
        useAppPermissions('skill', 'management');

    if (!canCreate) return <FourOFourPage />;

    const handleSubmit = async (values: TSkillFormValues) => {
        const { groups, certificates, thumbnail, ...otherValues } = values;

        const groupIds = groups?.map((group) => group.id);
        const certificateIds = certificates?.map(
            (certificate) => certificate.id
        );

        try {
            const response = await createSkill({
                variables: {
                    skill: {
                        groupIds,
                        certificateIds,
                        thumbnailId: thumbnail?.id || undefined,
                        ...otherValues,
                    },
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'skills' });
                },
            });

            const skillId = response.data?.createSkill?.skill?.id;

            onCreate?.(skillId, values);
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('skillsManagementPage.actionSuccess.create'),
            {
                variant: 'success',
            }
        );

        onClose?.();

        return;
    };

    const initialValues: TSkillFormValues = {
        name: '',
        shortName: '',
        description: '',
    };

    return (
        <PageDrawer open={open} onClose={onClose} {...other}>
            <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 0, sm: 0 } }}>
                <SkillForm
                    disabled={createSkillLoading}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                />
            </Box>
        </PageDrawer>
    );
};
